import {
    CasMaster,
    CasProperty,
    FormulaProperty,
} from '../../../../../../../types/formulation';
import React, { useContext, useEffect, useState } from 'react';
import { PermissionsUtil } from '../../../../../../../utils/permissions/permissionsUtil';
import { PERMISSIONS } from '../../../../../../../constants/permissions/Permissions.constants';
import { GridOptions } from '../../../../../../../components/grids/Grid.constants';
import { isNilOrEmpty } from '../../../../../../../utils/objectUtils';
import { RowStatus } from '../../../../../../../components/grids/hooks/useBaseGridEditable';
import _ from 'lodash';
import { ColumnMaxValueValidator } from '../../../../../../grid/validators/columnMaxValue.validator';
import applyEditableGridValidations from '../../../../../../grid/utils/applyEditableGridValidations';
import { useListUnitOfMeasureDropdownQuery } from '../../../../../../../services/organizations/organizations.service';
import { ColumnNumericValueValidator } from '../../../../../../grid/validators/columnNumericValue.validator';
import { ColumnMinValueValidator } from '../../../../../../grid/validators/columnMinValue.validator';
import { useListFormulaPropertyQuery } from '../../../../../../../services/formulation/formulaProperty/formulaProperty.service';
import { SelectedDropdownOption } from '../../../../../../../types/Shared.types';
import { ChildEditableGridWithCopyFunctionProps } from '../../../../../../grid/utils/editableGrid/ChildEditableGridWithCopyFunctionProps';
import SettingsContext from '../../../../../../../contexts/settings.context';
import { useGetTermSetQuery } from '../../../../../../../services/i18n/i18n.service';
import { FORMULATION_DEFS } from '../../../../../../../constants/i18n/translations/termSetDefinitions/formulation';
import { skipToken } from '@reduxjs/toolkit/query';
import CustomHeader from '../../../../../../../components/grids/CustomHeader';
import { CasMasterFormDefs } from '../../../../../../../constants/i18n/translations/termDefinitions/formulation';

const useCasPropertiesGrid = (
    props: ChildEditableGridWithCopyFunctionProps<CasMaster, CasProperty>
) => {
    const { currentParentRecord, user } = props.parentData;

    const { showDeleteButton, isGridEditable } = props.displayGridButtons;

    const { handleChildrenRecords } = props.helpers;

    const { rowsData, setRowsData } = props.copyMethods;

    const [maxRowId, setMaxRowId] = useState(0);

    const {
        data: formulaPropertyOptions,
        isLoading: isLoadingFormulaProperties,
    } = useListFormulaPropertyQuery();

    const { data: unitOfMeasureOptions, isLoading: isLoadingUnitOfMeasure } =
        useListUnitOfMeasureDropdownQuery();

    const [showMultiSelectGridModal, setShowMultiSelectGridModal] =
        useState(false);

    const [formulaPropertyList, setFormulaPropertyList] = useState(
        [] as FormulaProperty[]
    );

    const [casPropertiesColDef, setCasPropertiesColDef] = useState(null);

    const [unitOfMeasureColumnDefs, setUnitOfMeasureColumnDefs] =
        useState(null);

    const canViewUOM = PermissionsUtil.isPermissionEnabled(
        user.permissions,
        PERMISSIONS.SETUP.UNITS_OF_MEASURE.VIEW
    );

    const canViewProperties = PermissionsUtil.isPermissionEnabled(
        user.permissions,
        PERMISSIONS.FORMULATION.FORMULA_PROPERTIES.VIEW
    );

    const colDefOptions = {
        ...GridOptions.sortFilterAndWrapColumns,
        floatingFilter: true,
    };

    const { settings } = useContext(SettingsContext);
    const { data: termSet } = useGetTermSetQuery(
        settings?.userSettings
            ? {
                  languageId: settings?.userSettings?.languageId,
                  code: FORMULATION_DEFS.CAS_MASTER_FORM,
              }
            : skipToken
    );

    const getUnitCode = (unitId: bigint): SelectedDropdownOption => {
        let unitCode: SelectedDropdownOption = { id: null, display: '' };
        if (!isNilOrEmpty(unitId)) {
            unitOfMeasureOptions.find((current: any) => {
                if (current.id === unitId) {
                    unitCode = {
                        id: current.id,
                        display: current.label,
                    };
                }
            });
        }
        return unitCode;
    };

    const setFilteredFormulaPropertyList = () => {
        if (canViewProperties) {
            let filteredList: FormulaProperty[] = formulaPropertyOptions;

            _.forEach(rowsData, function (propertyRecord: CasProperty) {
                const matchingRecord = _.find(
                    formulaPropertyOptions,
                    (current: FormulaProperty) => {
                        return (
                            current.propertyCode ===
                                propertyRecord.propertyCode &&
                            propertyRecord.rowStatus !== RowStatus.DELETED
                        );
                    }
                );

                if (matchingRecord) {
                    filteredList = _.without(filteredList, matchingRecord);
                }
            });
            setFormulaPropertyList(filteredList);
        } else {
            setFormulaPropertyList([]);
        }
    };

    const handleSelectedRecords = (selectedRecords: FormulaProperty[]) => {
        const newRows: CasProperty[] = [];
        let index = maxRowId;

        selectedRecords?.forEach((record: FormulaProperty) => {
            index = index + 1;
            setMaxRowId((maxRowId) => maxRowId + 1);
            const updatedRecord: CasProperty = { ...record };
            updatedRecord.rowId = index;
            updatedRecord.rowStatus = RowStatus.ADDED;
            updatedRecord.propertyId = record.id;
            updatedRecord.propertyValue = record.defaultValue
                ? record.defaultValue
                : '';
            updatedRecord.unitCode = { id: null, display: '' };
            updatedRecord.propertyUnitId = null;
            updatedRecord.sortBy = null;
            delete updatedRecord.id;

            newRows.push(updatedRecord);
        });

        setRowsData((previousValues: CasProperty[]) => {
            const filteredPreviousList = previousValues.filter(
                (current: any) => current.rowStatus !== RowStatus.DELETED
            );
            return [...filteredPreviousList, ...newRows];
        });
        handleRowValidations(newRows);
        handleGridEdits(newRows);
    };

    const sortCasProperties = (casProperties: CasProperty[]) => {
        setRowsData(
            _.orderBy(casProperties, ['sortBy', 'propertyCode'], ['asc'])
        );
    };

    useEffect(() => {
        const includeEditedRows = currentParentRecord?.casProperties
            ? !isNilOrEmpty(
                  currentParentRecord.casProperties.find(
                      (current) => current.rowStatus
                  )
              )
            : false;
        if (
            currentParentRecord?.casProperties &&
            !isLoadingUnitOfMeasure &&
            !isNilOrEmpty(unitOfMeasureOptions) &&
            !includeEditedRows
        ) {
            const properties: CasProperty[] = [];
            let index = maxRowId;

            currentParentRecord?.casProperties.forEach(
                (casProperty: CasProperty) => {
                    index = index + 1;
                    setMaxRowId((maxRowId) => maxRowId + 1);

                    if (casProperty.rowStatus !== RowStatus.DELETED) {
                        const formulaProperty: FormulaProperty =
                            casProperty.property;
                        const newCasProperty = {
                            ...casProperty,
                            propertyCode: formulaProperty
                                ? formulaProperty.propertyCode
                                : casProperty.propertyCode,
                            description: formulaProperty
                                ? formulaProperty.description
                                : casProperty.description,
                            unitCode: getUnitCode(casProperty.propertyUnitId),
                            rowId: casProperty.rowId
                                ? casProperty.rowId
                                : index,
                        };
                        delete newCasProperty.property;
                        properties.push(newCasProperty);
                    }
                }
            );
            sortCasProperties(properties);
        }
    }, [
        currentParentRecord?.casProperties,
        isLoadingUnitOfMeasure,
        unitOfMeasureOptions,
    ]);

    useEffect(() => {
        if (!isLoadingUnitOfMeasure && !isNilOrEmpty(unitOfMeasureOptions)) {
            setUnitOfMeasureColumnDefs([
                {
                    field: 'unitCode',
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={
                                    CasMasterFormDefs.Property_Selection_UOM
                                }
                            />
                        );
                    },
                    cellClass: 'ag-left-aligned-cell',
                },
                {
                    field: 'description',
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={
                                    CasMasterFormDefs.Property_Selection_Description
                                }
                            />
                        );
                    },
                    cellClass: 'ag-left-aligned-cell',
                },
            ]);
        }
    }, [isLoadingUnitOfMeasure, unitOfMeasureOptions]);

    useEffect(() => {
        if (
            !isLoadingFormulaProperties &&
            !isNilOrEmpty(formulaPropertyOptions) &&
            !isLoadingUnitOfMeasure &&
            !isNilOrEmpty(unitOfMeasureOptions) &&
            !isNilOrEmpty(unitOfMeasureColumnDefs)
        ) {
            setCasPropertiesColDef([
                {
                    field: 'deleteColumn',
                    minWidth: 50,
                    hide: !showDeleteButton || !isGridEditable,
                    filter: false,
                },
                {
                    field: 'propertyCode',
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={CasMasterFormDefs.Property_Code}
                            />
                        );
                    },
                    editable: false,
                    filter: 'agTextColumnFilter',
                },
                {
                    field: 'description',
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={CasMasterFormDefs.Property_Name}
                            />
                        );
                    },
                    editable: false,
                    filter: 'agTextColumnFilter',
                },
                {
                    field: 'propertyValue',
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={CasMasterFormDefs.Property_Value}
                            />
                        );
                    },
                    editable: isGridEditable,
                    filter: 'agTextColumnFilter',
                },
                {
                    field: 'unitCode',
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={CasMasterFormDefs.Property_UOM}
                            />
                        );
                    },
                    editable: canViewUOM && isGridEditable,
                    cellRenderer: (params: any) => params.value.display,
                    useGridPopup: true,
                    gridPopupParameters: {
                        isLoading: isLoadingUnitOfMeasure,
                        displayGrid: true,
                        rowData: canViewUOM ? unitOfMeasureOptions : [],
                        defaultColDef: colDefOptions,
                        columnDefs: unitOfMeasureColumnDefs,
                        displayField: 'label',
                    },
                    filter: 'agTextColumnFilter',
                },
            ]);
        }
    }, [
        isLoadingUnitOfMeasure,
        unitOfMeasureOptions,
        unitOfMeasureColumnDefs,
        isLoadingFormulaProperties,
        formulaPropertyOptions,
    ]);

    useEffect(() => {
        setFilteredFormulaPropertyList();
    }, [isLoadingFormulaProperties, formulaPropertyOptions, rowsData]);

    useEffect(() => {
        if (
            !isLoadingFormulaProperties &&
            isNilOrEmpty(formulaPropertyOptions)
        ) {
            setFilteredFormulaPropertyList();
            if (currentParentRecord && currentParentRecord?.casProperties) {
                setRowsData(currentParentRecord.casProperties);
            }
        }
    }, [
        isLoadingFormulaProperties,
        formulaPropertyOptions,
        currentParentRecord?.casProperties,
        rowsData,
    ]);

    const handleRowValidations = (editedRows: CasProperty[]) => {
        editedRows?.forEach((row: CasProperty) => {
            row.validationRules = [
                ColumnMaxValueValidator(
                    'Property Value',
                    25,
                    row.propertyValue
                ),
                ColumnNumericValueValidator('Sort By', 0, row.sortBy),
                ColumnMinValueValidator('Sort By', 0, Number(row.sortBy)),
            ];
            applyEditableGridValidations(row, `Property ${row.propertyCode}`);
        });
        return editedRows;
    };

    const handleGridEdits = (editedRows: CasProperty[]) => {
        const updatedFields: CasProperty[] = [...rowsData];

        editedRows?.forEach((casProperty: CasProperty) => {
            const index = rowsData?.findIndex(
                (row: CasProperty) =>
                    row.propertyCode === casProperty.propertyCode &&
                    row.rowId === casProperty.rowId
            );

            if (!isNilOrEmpty(casProperty.unitCode)) {
                casProperty.propertyUnitId = casProperty.unitCode?.id;
            }

            if (isNilOrEmpty(casProperty.sortBy)) {
                casProperty.sortBy = null;
            }

            if (index > -1) {
                updatedFields[index] = casProperty;
            } else {
                updatedFields.push(casProperty);
            }
        });
        sortCasProperties(updatedFields);
        handleChildrenRecords('casProperties', updatedFields);
    };

    return {
        handleGridEdits,
        rowsData,
        formulaPropertyList,
        showMultiSelectGridModal,
        setShowMultiSelectGridModal,
        handleSelectedRecords,
        casPropertiesColDef,
        handleRowValidations,
        colDefOptions,
        setFilteredFormulaPropertyList,
        termSet,
    };
};

export default useCasPropertiesGrid;
